import { Box, Button, Grid, LinearProgress, TextField } from '@mui/material';
import { ApolloQueryResult, OperationVariables, useMutation } from '@parkchirp/api-client';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { SAVE_CREDIT_CARD } from '../../../queries';
import type { SetShowSnackbar } from '../../../types';
import { isValidPostalCode } from '../../../utils';
import { createGPIError } from '../../../utils/gpiErrors';
import GPICreditCardFields from './gpiCreditCardFields';

declare global {
	interface Window {
		GlobalPayments: any;
	}
}

interface CardProps {
	cognitoSub: string;
	setTemporaryToken?: (token: string) => void;
	isProcessPayment?: boolean;
	saveNewCard?: boolean;
	onSubmit?: (event: any, token: string) => void;
	payInvoiceLoading?: boolean;
	setShowSnackbar: SetShowSnackbar;
	gpiFormConfig: Record<string, any>;
	tenantSubscriptionId?: string;
	refetchPaymentMethods?: (variables?: Partial<OperationVariables>) => Promise<ApolloQueryResult<any>>;
	CancelButton?: any;
}

// Code copied over from admin dashboard for re-use
export const GPICreditCardForm: FC<CardProps> = ({
	cognitoSub,
	setTemporaryToken,
	isProcessPayment,
	saveNewCard,
	onSubmit,
	payInvoiceLoading,
	setShowSnackbar,
	gpiFormConfig,
	tenantSubscriptionId,
	refetchPaymentMethods,
	CancelButton,
}) => {
	const [streetAddress, setStreetAddress] = useState<string>();
	const [zip, setZip] = useState<string>();
	const [response, setResponse] = useState<any>(null);
	const [isGPILoaded, setIsGPILoaded] = useState<boolean>(false);

	const [savePaymentToken, { loading: saveCardLoading }] = useMutation(SAVE_CREDIT_CARD, {
		onCompleted: () => {
			setShowSnackbar({
				message: 'Card added to your account.',
				severity: 'success',
				autoHideDuration: 5000,
				onClose: () => {
					setShowSnackbar(null);
				},
			});
			setStreetAddress('');
			setZip('');

			if (refetchPaymentMethods) {
				refetchPaymentMethods();
			}
		},
	});

	const handleValidation = useCallback(() => {
		const requiredFields = [
			{ label: 'Street Address', value: streetAddress },
			{ label: 'Zip Code', value: zip },
		];

		const missingFields = requiredFields.filter(({ value }) => ['', null, undefined].includes(value));
		if (missingFields.length) {
			const missingAsString = missingFields?.map(({ label }) => label).join(', ');
			setShowSnackbar({
				message: `Unable to add due to missing field(s): ${missingAsString}`,
				severity: 'error',
				autoHideDuration: 6000,
				onClose: () => {
					setShowSnackbar(null);
				},
			});
			return false;
		}

		if (!isValidPostalCode(zip)) {
			setShowSnackbar({
				message: 'Invalid Zip Code',
				severity: 'error',
				autoHideDuration: 3000,
				onClose: () => {
					setShowSnackbar(null);
				},
			});

			return false;
		}

		return true;
	}, [streetAddress, zip, setShowSnackbar]);

	useEffect(() => {
		const scriptId = 'global-payments-script';
		let script = document.getElementById(scriptId) as HTMLScriptElement;

		if (!script) {
			script = document.createElement('script');
			script.id = scriptId;
			script.src = 'https://js.paygateway.com/secure_payment/v1/globalpayments.js';
			script.async = true;
			script.onload = () => {
				window.GlobalPayments.configure({
					'X-GP-Api-Key': process.env.NEXT_PUBLIC_REACT_APP_GPI_TOKENIZATION_API_KEY,
					'X-GP-Environment': process.env.NEXT_PUBLIC_REACT_APP_GPI_ENVIRONMENT,
					enableAutocomplete: true,
				});
				setIsGPILoaded(true);
			};

			script.onerror = () => {
				setShowSnackbar({
					message: 'Error loading the Global Payments script.',
					severity: 'error',
					autoHideDuration: 5000,
					onClose: () => setShowSnackbar(null),
				});
			};

			document.body.appendChild(script);
		}

		return () => {
			if (script) {
				script.remove();
			}
		};
	}, []);

	useEffect(() => {
		if (!isGPILoaded) {
			return undefined;
		}

		const cardForm = window.GlobalPayments?.ui?.form(gpiFormConfig);

		cardForm?.on('token-success', async (res: { temporary_token: string }) => {
			setResponse(res);

			if (res?.temporary_token && setTemporaryToken) {
				setTemporaryToken(res.temporary_token);
			}
		});

		cardForm.on(
			'token-error',
			(res: { error: { message: string; detail: { description: string; data_path: string }[] } }) => {
				const error = createGPIError({
					rawError: res,
					code: 'tokenization_error',
					clientType: 'TOKENIZER',
				});
				setShowSnackbar({
					message: error.message,
					severity: 'error',
					autoHideDuration: 10000,
					onClose: () => {
						setShowSnackbar(null);
					},
				});
			},
		);

		return () => {
			cardForm?.destroy?.();
		};
	}, [isGPILoaded]);

	useEffect(() => {
		const handleCardSubmit = async (res: { temporary_token: string }) => {
			try {
				if (!handleValidation()) return;

				if (isProcessPayment && saveNewCard) {
					const savedTokenData = await savePaymentToken({
						variables: {
							token: {
								token: res?.temporary_token,
								street: streetAddress,
								zip,
							},
							tenantSubscriptionId,
							cognitoSub,
						},
					});

					const savedToken = savedTokenData?.data?.savePaymentToken;

					if (savedToken) {
						if (setTemporaryToken) {
							setTemporaryToken(savedToken);
						}

						if (onSubmit) {
							onSubmit(null, savedToken);
						}
					}
				} else if (isProcessPayment && !saveNewCard) {
					if (onSubmit) {
						onSubmit(res, res?.temporary_token);
					}
				} else {
					await savePaymentToken({
						variables: {
							token: {
								token: res?.temporary_token,
								street: streetAddress,
								zip,
							},
							tenantSubscriptionId,
							cognitoSub,
						},
					});
				}

				setIsGPILoaded(false);
			} catch (error: any) {
				setShowSnackbar({
					message: error.message,
					severity: 'error',
					autoHideDuration: 3000,
					onClose: () => {
						setShowSnackbar(null);
					},
				});
			}
		};

		if (response) {
			handleCardSubmit(response);
			setResponse(null);
		}
	}, [
		response,
		handleValidation,
		savePaymentToken,
		cognitoSub,
		setShowSnackbar,
		streetAddress,
		zip,
		isProcessPayment,
		saveNewCard,
		onSubmit,
		setTemporaryToken,
		tenantSubscriptionId,
	]);

	return (
		<Box className='pca__credit-card-form' sx={{ maxWidth: '650px' }}>
			<GPICreditCardFields />
			<Grid container spacing={2} marginTop={0}>
				<Grid item xs={6}>
					<TextField
						required
						fullWidth
						variant='outlined'
						label='Street Address'
						InputLabelProps={{ shrink: true }}
						value={streetAddress || ''}
						onChange={(e) => setStreetAddress(e.target.value)}
						data-cy='street-address-input'
					/>
				</Grid>
				<Grid item xs={3}>
					<TextField
						required
						fullWidth
						variant='outlined'
						label='Zip/Postal Code'
						InputLabelProps={{ shrink: true }}
						value={zip || ''}
						onChange={(e) => setZip(e.target.value)}
						data-cy='zip-code-input'
					/>
				</Grid>
			</Grid>
			<Box sx={{ mt: 3, display: 'flex' }}>
				<div style={{ display: 'flex', alignItems: 'center' }}>
					<Button
						loading={saveCardLoading || payInvoiceLoading || false}
						sx={{
							backgroundColor: 'transparent !important',
							border: '0px',
							borderRadius: '5px',
							'&:hover': {
								cursor: 'pointer',
								boxShadow: 'none',
							},
						}}
						id='submit-gpi'
					/>
					{CancelButton ? <CancelButton /> : null}
				</div>
				<div id='submit-gpi'>
					<LinearProgress color='primary' data-cy='loading-indicator' />
				</div>
			</Box>
		</Box>
	);
};
